import React, { useState } from 'react';
import { Scissors, Printer, ChevronDown } from 'lucide-react';
import { photoBlockCuttingList, formatBoxes, CARD_LAYOUT_QUERY } from './productionUtils';
import { matchesMedia } from '../../utils/breakpoints';

// MDF cutting list for the Photo Blocks work orders on screen, at the quantity still to
// make. Renders nothing when there are no blocks left to make. Where the work orders show
// as cards (phones, tablets) it starts collapsed to a one-line summary so the work orders
// stay near the top; on desktop it starts open.
const CuttingList = ({ entries, onPrint }) => {
  const [expanded, setExpanded] = useState(() => !matchesMedia(CARD_LAYOUT_QUERY));
  const boards = photoBlockCuttingList(entries);
  if (boards.length === 0) return null;

  const totalSheets = boards.reduce((sum, board) => sum + board.sheets, 0);

  return (
    <section className={`cutting-list ${expanded ? 'expanded' : 'collapsed'}`} aria-label="MDF cutting list">
      <div className="cutting-list-header">
        <h3 className="cutting-list-heading">
          <button
            type="button"
            className="cutting-list-toggle"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
            aria-controls="cutting-list-boards"
          >
            <Scissors size={14} aria-hidden="true" />
            <span className="cutting-list-title">MDF cutting list</span>
            <span className="cutting-list-summary">
              {totalSheets} {totalSheets === 1 ? 'sheet' : 'sheets'} to pull
            </span>
            <ChevronDown size={16} className="cutting-list-chevron" aria-hidden="true" />
          </button>
        </h3>
        {expanded && <span className="cutting-list-scope">Still to make on the work orders below</span>}
        <button type="button" className="cutting-list-print" onClick={() => onPrint(boards)}>
          <Printer size={14} /> Print
        </button>
      </div>
      <div className="cutting-list-boards" id="cutting-list-boards" hidden={!expanded}>
        {boards.map(board => {
          const pull = board.sheets;
          return (
            <div key={board.code} className="cutting-list-card">
              <div className="cutting-list-card-header">
                <span className="cutting-list-board-name">{board.name}</span>
                <span className="cutting-list-code">{board.code}</span>
                <span className="cutting-list-pull">{pull} {pull === 1 ? 'sheet' : 'sheets'} to pull</span>
              </div>
              <table className="cutting-list-board">
                <thead>
                  <tr>
                    <th>Size</th>
                    <th>Blocks</th>
                    <th>Boxes</th>
                    <th>Sheets</th>
                  </tr>
                </thead>
                <tbody>
                  {board.rows.map(row => (
                    <tr key={row.size}>
                      <td>{row.size}</td>
                      <td>{row.blocks.toLocaleString()}</td>
                      <td title={`${row.perBox} per box`}>{formatBoxes(row.fullBoxes, row.loose)}</td>
                      <td title={`${Number(row.exactSheets.toFixed(2))} exactly`}>{row.sheets}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{board.blocks.toLocaleString()}</td>
                    <td>{formatBoxes(board.fullBoxes, board.loose)}</td>
                    <td>{board.sheets}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CuttingList;
